<template>
  <v-card :loading="loading" class="mx-5 pa-5">
    <div class="d-flex flex-column justify-center align-center">
      <v-avatar size="120">
        <img
          :src="
            (slot.booker && slot.booker.avatar) || require('@/assets/face.jpg')
          "
          alt="Ảnh đại diện"
        />
      </v-avatar>
      <div class="text-h3 mt-3">
        {{ slot.booker ? slot.booker.name : "" }}
      </div>
      <div class="text-h4 mt-5">
        <span class="font-weight-bold mr-3">Ngày:</span>
        <span class="mr-15">{{ formatDate(slot.startTime) }}</span>
        <span class="font-weight-bold mr-3">Giờ:</span>
        <span>{{ formatTime(slot.startTime) }}</span>
      </div>
    </div>
    <v-col cols="6" v-if="slot.testResult">
      <v-row>
        <v-col
          cols="9"
          class="d-flex text-h3 align-center text-decoration-underline blue--text text--darken-3 font-weight-medium"
        >
          Kết quả thang đánh giá
        </v-col>
        <v-col cols="3" class="d-flex justify-end"
          ><v-btn text large color="warning" @click="showAnswers = !showAnswers"
            >Chi tiết</v-btn
          ></v-col
        >
      </v-row>
      <v-list>
        <div class="result-row">
          <div class="result-row-label">Stress</div>
          <div :style="`color: ${stressColor};`" class="result-row-value">
            {{ stressText }}
          </div>
        </div>
        <div class="result-row">
          <div class="result-row-label">Trầm cảm</div>
          <div :style="`color: ${depressColor};`" class="result-row-value">
            {{ depressText }}
          </div>
        </div>
        <div class="result-row">
          <div class="result-row-label">Lo âu</div>
          <div :style="`color: ${anxietyColor};`" class="result-row-value">
            {{ anxietyText }}
          </div>
        </div>
        <v-list-item
          v-for="item in slot.testResult.answers"
          :key="item._id"
          v-show="showAnswers"
        >
          <v-list-item-title class="text-h4" v-text="item.question" />
          <v-list-item-action>
            <div>{{ item.answer }}</div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-col>
    <v-col cols="6">
      <div
        class="text-h3 text-decoration-underline blue--text text--darken-3 font-weight-medium"
      >
        Tài liệu
      </div>
      <v-list>
        <v-list-item v-for="item in slot.documents" :key="item._id">
          <v-list-item-avatar>
            <v-icon color="primary">
              mdi-download-circle
            </v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title
              class="link-downloadable text-decoration-underline text-h4 primary--text"
              @click="download(item)"
              v-text="item.name"
            />
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon color="pink" @click="removeDocument(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-file-input
        label="Thêm tài liệu"
        outlined
        dense
        @change="onFileChange"
      />
      <v-btn
        small
        color="#4CAF50"
        class="ml-8"
        :disabled="!file"
        :loading="loadingUpload"
        @click="addDocument"
      >
        Tải lên
      </v-btn>
    </v-col>
    <div>
      <v-data-table
        :headers="headers"
        :items="slot.notes"
        loading-text="Đang tải... Vui lòng chờ"
        hide-default-footer
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <div
              class="text-h3 text-decoration-underline blue--text text--darken-3 font-weight-medium ml-n5"
            >
              Ghi chú
            </div>
            <v-spacer />
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  dark
                  class="mb-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  Thêm ghi chú
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-text-field v-model="editedNote.title" label="Tiêu đề" />
                    <v-textarea
                      v-model="editedNote.content"
                      label="Nội dung"
                      auto-grow
                    />
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn color="blue darken-1" text @click="close">
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    :loading="loadingUpdate"
                    @click="save"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:[`item.content`]="{ item }">
          <div class="slot-table-cell">
            {{ item.content }}
          </div>
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatFullDate(item.createdAt) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon small class="mr-2 info--text" @click="editItem(item)">
            mdi-pencil
          </v-icon>
          <v-icon small class="error--text" @click="deleteItem(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import BookingSlotApi from "@/api/BookingSlotApi";
import axios from "axios";
import {
  ratingColors,
  ratingLabels,
  stressLevel,
  anxietyLevel,
  depressLevel
} from "../../../utilities/utilities.js";

export default {
  data() {
    return {
      dialog: false,
      headers: [
        { text: "Tiêu đề", value: "title", width: 300 },
        { text: "Nội dung", value: "content", class: "slot-table-cell" },
        { text: "Thời gian tạo", value: "createdAt", width: 200 },
        { text: "Actions", value: "actions", sortable: false, width: 100 }
      ],
      editedIndex: -1,
      editedNote: {
        title: "",
        content: ""
      },
      defaultNote: {
        title: "",
        content: ""
      },
      loading: true,
      loadingUpdate: false,
      loadingUpload: false,
      slot: {},
      showAnswers: false,
      file: undefined
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex > -1 ? "Sửa ghi chú" : "Thêm ghi chú";
    },
    stressText() {
      return ratingLabels[stressLevel(this.slot?.testResult?.stress)];
    },
    depressText() {
      return ratingLabels[depressLevel(this.slot?.testResult?.depression)];
    },
    anxietyText() {
      return ratingLabels[anxietyLevel(this.slot?.testResult?.anxiety)];
    },
    stressColor() {
      return ratingColors[stressLevel(this.slot?.testResult?.stress)];
    },
    depressColor() {
      return ratingColors[depressLevel(this.slot?.testResult?.depression)];
    },
    anxietyColor() {
      return ratingColors[anxietyLevel(this.slot?.testResult?.anxiety)];
    }
  },
  async mounted() {
    this.loading = true;
    const response = await BookingSlotApi.slotDetail(this.$route.params.id);
    this.loading = false;
    const { ok, data } = response;
    if (ok) {
      this.slot = data;
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("vi");
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString("vi");
    },
    formatFullDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString("vi");
    },
    editItem(item) {
      this.editedIndex = this.slot.notes.indexOf(item);
      this.editedNote = Object.assign({}, item);
      this.dialog = true;
    },
    async deleteItem(item) {
      const index = this.slot.notes.indexOf(item);
      const agree = confirm("Bạn có chắc muốn xoá ghi chú?");
      if (agree) {
        const res = await BookingSlotApi.deleteNote(this.slot._id, item._id);
        if (res.ok) {
          this.slot.notes.splice(index, 1);
        } else {
          alert("Xoá người dùng không thành công");
        }
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedNote = Object.assign({}, this.defaultNote);
        this.editedIndex = -1;
      });
    },
    async save() {
      if (this.editedIndex > -1) {
        const { title, content } = this.editedNote;
        this.loadingUpdate = true;
        const res = await BookingSlotApi.updateNote(
          this.slot._id,
          this.editedNote._id,
          {
            title,
            content
          }
        );
        this.loadingUpdate = false;
        const { ok, data } = res;
        if (ok) {
          Object.assign(this.slot.notes[this.editedIndex], data);
        } else {
          alert("Cập nhật thông tin không thành công!");
        }
      } else {
        this.loadingUpdate = true;
        const res = await BookingSlotApi.createNote(this.slot._id, {
          ...this.editedNote
        });
        this.loadingUpdate = false;
        const { ok, data } = res;
        if (ok) {
          this.slot.notes.push(data);
        } else {
          alert("Tạo ghi chú mới không thành công!");
        }
      }
      this.close();
    },
    async download(item) {
      const { url, name } = item;
      axios.get(url, { responseType: "blob" }).then(response => {
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = name;
        link.click();
        URL.revokeObjectURL(link.href);
      });
    },
    onFileChange(file) {
      this.file = file;
    },
    async addDocument() {
      this.loadingUpload = true;
      const res = await BookingSlotApi.addDocument(this.slot._id, this.file);
      this.loadingUpload = false;
      const { ok, data } = res;
      if (ok) {
        this.slot.documents.push(data);
        this.file = undefined;
      } else {
        alert("Thêm tài liệu mới không thành công!");
      }
    },
    async removeDocument(item) {
      const index = this.slot.documents.indexOf(item);
      const agree = confirm("Bạn có chắc muốn xoá tài liệu này?");
      if (agree) {
        const res = await BookingSlotApi.deleteDocument(
          this.slot._id,
          item._id
        );
        if (res.ok) {
          this.slot.documents.splice(index, 1);
        } else {
          alert("Xoá tài liệu không thành công");
        }
      }
    }
  }
};
</script>
<style lang="sass">
.slot-table-cell
  white-space: pre-wrap

.result-row
  display: flex
  font-size: 20px

.result-row-label
  width: 150px

.link-downloadable
  cursor: pointer
</style>
